import { useState, useId } from 'react';

// ─── LineChart ────────────────────────────────────────────────────────────────
//
// Kompaktes, interaktives Liniendiagramm (SVG). Zeigt einen Wert je Kategorie
// (z. B. Ø-Alter je Jahrzehnt) als Linie mit Fläche und Punkten. Hovern bzw.
// Tippen auf einen Punkt zeigt Wert und optionale Fallzahl (n). Bei vielen
// Punkten scrollt das Diagramm horizontal in seinem Container.

export interface LinePoint {
  label: string;
  value: number;
  /** Optionale Fallzahl für den Tooltip (z. B. Anzahl Personen). */
  count?: number;
}

interface LineChartProps {
  data: LinePoint[];
  /** Einheit hinter dem Wert im Tooltip (z. B. „J."). */
  unit?: string;
  color?: string;
  height?: number;
}

export function LineChart({ data, unit = '', color = '#6366f1', height = 210 }: LineChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const baseId = useId();

  if (data.length === 0) return null;

  const padL = 34;
  const padR = 14;
  const padT = 18;
  const padB = 28;
  // Mindestens 64px pro Punkt, damit die Labels lesbar bleiben.
  const width = Math.max(data.length * 64 + padL + padR, 300);
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const values = data.map((d) => d.value);
  let yMin = Math.min(...values);
  let yMax = Math.max(...values);
  // Etwas Luft und auf 5er runden – die Trendkurve soll erkennbar sein.
  const span = yMax - yMin;
  const pad = Math.max(2, Math.round(span * 0.15));
  yMin = Math.max(0, Math.floor((yMin - pad) / 5) * 5);
  yMax = Math.ceil((yMax + pad) / 5) * 5;
  if (yMax === yMin) yMax = yMin + 5;

  const x = (i: number) =>
    padL + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const y = (v: number) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(d.value)}`).join(' ');
  const areaPath = `${linePath} L${x(data.length - 1)},${padT + innerH} L${x(0)},${padT + innerH} Z`;

  const TICKS = 4;
  const yTicks = Array.from({ length: TICKS + 1 }, (_, k) => yMin + (k / TICKS) * (yMax - yMin));

  const activePoint = active != null ? data[active] : null;

  return (
    <div className="overflow-x-auto">
      <svg
        width={width}
        height={height}
        className="max-w-none"
        role="img"
        aria-label={`Liniendiagramm: ${data.map((d) => `${d.label} ${d.value}${unit}`).join(', ')}`}
      >
        {/* Gitterlinien + Y-Beschriftung */}
        {yTicks.map((t, k) => (
          <g key={`${baseId}-tick-${k}`}>
            <line x1={padL} y1={y(t)} x2={width - padR} y2={y(t)} stroke="#f1f5f9" />
            <text x={padL - 6} y={y(t)} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="#94a3b8">
              {Math.round(t)}
            </text>
          </g>
        ))}

        {/* Fläche + Linie */}
        <path d={areaPath} fill={color} opacity={0.08} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {/* Aktiv-Markierung (senkrechte Hilfslinie) */}
        {activePoint && (
          <line
            x1={x(active!)}
            y1={padT}
            x2={x(active!)}
            y2={padT + innerH}
            stroke={color}
            strokeDasharray="3 3"
            opacity={0.4}
            pointerEvents="none"
          />
        )}

        {/* Punkte + X-Beschriftung + Hover-Ziele */}
        {data.map((d, i) => (
          <g
            key={`${baseId}-pt-${i}`}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            onClick={() => setActive((cur) => (cur === i ? null : i))}
            className="cursor-pointer"
          >
            <circle cx={x(i)} cy={y(d.value)} r={12} fill="transparent" />
            <circle cx={x(i)} cy={y(d.value)} r={active === i ? 5 : 3.5} fill={color} stroke="#fff" strokeWidth={1.5} />
            <text x={x(i)} y={height - 8} textAnchor="middle" fontSize={10} fill="#64748b">
              {d.label}
            </text>
          </g>
        ))}

        {/* Tooltip des aktiven Punkts – Randpunkte nach innen ausrichten,
            damit der Text nicht abgeschnitten wird. */}
        {activePoint && (() => {
          const tx = x(active!);
          const anchor = tx > width - padR - 60 ? 'end' : tx < padL + 60 ? 'start' : 'middle';
          return (
            <text
              x={tx}
              y={Math.max(y(activePoint.value) - 12, padT + 9)}
              textAnchor={anchor}
              fontSize={12}
              fontWeight={700}
              fill="#1e293b"
              pointerEvents="none"
            >
              {activePoint.value}
              {unit ? ` ${unit}` : ''}
              {activePoint.count != null ? ` (n=${activePoint.count})` : ''}
            </text>
          );
        })()}
      </svg>
    </div>
  );
}
